import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Zap, Sparkles, Shield, ArrowUpRight, Check, Receipt, Calendar, Gift, Copy, CheckCheck, Users } from "lucide-react";
import api from "@/lib/api";
import AIQuotaMeter from "@/components/AIQuotaMeter";

const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n);

const ICON_BY_PLAN = { free: Shield, pro: Zap, premium: Sparkles, founder: Crown };

export default function Membership() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/membership/me")
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Error al cargar membresía"));
  }, []);

  if (error) return <div className="container-app py-16"><p className="text-red-400">{error}</p></div>;
  if (!data) return <div className="container-app py-16"><p className="text-muted-foreground">Cargando…</p></div>;

  const plan = data.plan_definition;
  const Icon = ICON_BY_PLAN[data.plan] || Shield;
  const pctUsed = (used, total) => (total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0);

  return (
    <div className="container-app py-12 max-w-5xl" data-testid="membership-page">
      <div className="label-eyebrow">Mi membresía</div>
      <h1 className="serif text-4xl mt-3 leading-tight">Tu plan en Colega Link</h1>

      {/* Current plan card */}
      <div className={`mt-8 panel p-8 ${data.is_founder ? "ring-2 ring-amber-400/30" : ""}`} data-testid="current-plan-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <Icon size={28} className={data.is_founder ? "text-amber-400" : data.plan === "premium" ? "text-violet-400" : data.plan === "pro" ? "text-emerald-400" : "text-muted-foreground"} />
              <div>
                <div className="label-eyebrow">Plan actual</div>
                <h2 className="serif text-3xl">{plan.name}</h2>
              </div>
              {data.is_founder && (
                <span className="px-3 py-1 rounded-full bg-amber-400/20 border border-amber-400/40 text-amber-300 text-xs font-bold tracking-wider" data-testid="founder-badge">
                  FUNDADOR N°{data.founder_number}
                </span>
              )}
              {!data.is_founder && data.is_embajador_pionero && (
                <span className="px-3 py-1 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-300 text-xs font-bold tracking-wider" data-testid="pionero-badge">
                  PIONERO N°{data.early_reserve_number} · Cupo Fundador asegurado
                </span>
              )}
            </div>
            <div className="mt-4 serif text-4xl">{plan.price_clp === 0 ? "Gratis" : CLP(plan.price_clp)}<span className="text-sm text-muted-foreground font-sans"> / mes</span></div>
            <p className="mt-2 text-sm text-muted-foreground">Comisión {plan.commission_pct}% · {plan.priority_label}</p>
          </div>
          <div className="flex flex-col gap-3">
            <Link to="/planes" className="btn-primary" data-testid="upgrade-link">
              Cambiar de plan <ArrowUpRight size={16} className="ml-2" />
            </Link>
            {data.plan_renews_at && (
              <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                <Calendar size={12} /> Próxima renovación: {new Date(data.plan_renews_at).toLocaleDateString("es-CL")}
              </div>
            )}
          </div>
        </div>

        {/* Usage meters */}
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <div className="panel p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Receipt size={16} className="text-[hsl(var(--accent))]" /><span className="text-sm font-medium">Delegaciones gratis</span></div>
              <div className="text-xs text-muted-foreground">{data.free_delegations_used} / {plan.free_delegations} este mes</div>
            </div>
            <div className="mt-3 h-2 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${pctUsed(data.free_delegations_used, plan.free_delegations)}%` }} />
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Quedan <span className="text-foreground font-medium">{data.free_delegations_remaining}</span> con 0% comisión
            </div>
          </div>
          <div className="panel p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Sparkles size={16} className="text-cyan-300" /><span className="text-sm font-medium">Colega IA · Asistente legal</span></div>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-300 font-bold tracking-wider">ACTIVO</span>
            </div>
            <div className="mt-3 h-2 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-cyan-400/70" style={{ width: "100%" }} />
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Consultas incluidas mensualmente · pack extra disponible si necesitas más. Ver detalle abajo.
            </div>
          </div>
        </div>

        {/* Colega IA — consumo del período (solo abogados con membresía activa) */}
        <div className="mt-4">
          <AIQuotaMeter />
        </div>

        {/* Perks */}
        <div className="mt-8">
          <div className="label-eyebrow">Beneficios incluidos</div>
          <ul className="mt-3 grid sm:grid-cols-2 gap-2 text-sm">
            {plan.perks.map((p) => (
              <li key={p} className="flex items-start gap-2">
                <Check size={14} className="text-[hsl(var(--accent))] mt-0.5 shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Referral code panel — only for Founders */}
      {data.is_founder && data.referral_code && (
        <ReferralPanel data={data} />
      )}

      <p className="mt-6 text-xs text-muted-foreground">
        ¿Necesitas factura o un plan a medida para tu estudio? Escríbenos a <span className="text-foreground">contacto@colegalink.cl</span>.
      </p>
    </div>
  );
}

function ReferralPanel({ data }) {
  const [copied, setCopied] = useState(false);
  const [referrals, setReferrals] = useState(null);

  useEffect(() => {
    api.get("/referrals/mine").then((r) => setReferrals(r.data)).catch((e) => console.warn("referrals/mine:", e?.response?.status || e.message));
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.referral_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { console.warn("clipboard:", e?.message); }
  };
  const bonusCap = data.bonus_cap_per_period || 5;
  const bonusUsed = data.bonus_earned_this_period ?? 0;

  return (
    <div className="mt-6 panel p-8 ring-1 ring-amber-400/20" data-testid="referral-panel">
      <div className="flex items-start gap-3 flex-wrap">
        <Gift size={22} className="text-amber-400 mt-1" />
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow text-amber-300">Tu código de invitación · Beneficio Fundador</div>
          <h3 className="serif text-2xl mt-1">Invita colegas y gana más delegaciones gratis</h3>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            Comparte tu código. Cada amigo que se suscriba a Pro, Premium o Fundador recibe
            <span className="text-amber-300 font-medium"> 50% OFF su primer mes</span>. Por cada invitado,
            tú ganas <span className="text-amber-300 font-medium">+1 delegación gratis</span> ese mes (hasta {bonusCap}/mes).
          </p>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-3 gap-4">
        {/* Code display */}
        <div className="md:col-span-1 panel p-5 bg-amber-400/5 border border-amber-400/30">
          <div className="label-eyebrow text-amber-300">Tu código</div>
          <div className="mt-2 font-mono text-xl tracking-wider text-amber-200 break-all" data-testid="referral-code-value">{data.referral_code}</div>
          <button
            onClick={copy}
            className="mt-4 btn-ghost w-full text-sm inline-flex items-center justify-center gap-2"
            data-testid="copy-referral-code"
          >
            {copied ? <><CheckCheck size={14} className="text-emerald-400" /> ¡Copiado!</> : <><Copy size={14} /> Copiar código</>}
          </button>
        </div>

        {/* Stats */}
        <div className="md:col-span-2 grid grid-cols-3 gap-3">
          <div className="panel p-4 text-center">
            <Users size={16} className="mx-auto text-muted-foreground" />
            <div className="serif text-3xl mt-2" data-testid="referrals-total">{referrals?.total ?? 0}</div>
            <div className="text-[10px] mt-1 label-eyebrow">Invitados totales</div>
          </div>
          <div className="panel p-4 text-center">
            <Gift size={16} className="mx-auto text-amber-400" />
            <div className="serif text-3xl mt-2 text-amber-300">+{bonusUsed}<span className="text-base text-muted-foreground">/{bonusCap}</span></div>
            <div className="text-[10px] mt-1 label-eyebrow">Bonus este mes</div>
          </div>
          <div className="panel p-4 text-center">
            <Crown size={16} className="mx-auto text-amber-400" />
            <div className="serif text-3xl mt-2">{data.referrals_this_period ?? 0}</div>
            <div className="text-[10px] mt-1 label-eyebrow">Este mes</div>
          </div>
        </div>
      </div>

      {/* Referrals list */}
      {referrals && referrals.referrals.length > 0 && (
        <div className="mt-6">
          <div className="label-eyebrow mb-3">Historial de invitados</div>
          <ul className="space-y-2">
            {referrals.referrals.slice(0, 5).map((r) => (
              <li key={r.id} className="panel p-3 flex items-center justify-between gap-3 text-sm" data-testid={`referral-row-${r.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-amber-400/15 flex items-center justify-center shrink-0">
                    <Users size={14} className="text-amber-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{r.referee_name}</div>
                    <div className="text-xs text-muted-foreground">Plan {r.plan_to.toUpperCase()} · {new Date(r.created_at).toLocaleDateString("es-CL")}</div>
                  </div>
                </div>
                {r.bonus_awarded && (
                  <span className="pill text-[10px] bg-amber-400/15 text-amber-300 border-amber-400/30 shrink-0">+1 deleg. gratis</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(!referrals || referrals.referrals.length === 0) && (
        <p className="mt-6 text-xs text-muted-foreground italic">
          Aún no has invitado a nadie. Comparte tu código por WhatsApp, correo o redes para empezar a ganar bonus.
        </p>
      )}
    </div>
  );
}
